import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import {
    Users,
    Copy,
    Share2,
    Gift,
    TrendingUp,
    DollarSign,
    UserPlus,
    Award,
    CheckCircle,
    Clock,
    Search,
    Filter,
    SortAsc,
    SortDesc,
    X
} from 'lucide-react';
import Header from '@/react-app/components/Header';
import type { ReferralDataResponse } from '@/shared/types';
import { api } from '@/shared/api/endpoints';
import { useNavigate } from "react-router-dom";



export default function Referrals() {
    const { user: authStoreUser, isAuthenticated } = useAuthStore();
    const [referralStats, setReferralStats] = useState<ReferralDataResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [copySuccess, setCopySuccess] = useState(false);
    // const [shareSuccess, setShareSuccess] = useState(false);
    const navigate = useNavigate();

    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending'>('all');
    const [dateFilter, setDateFilter] = useState<'all' | 'week' | 'month' | 'quarter'>('all');
    const [sortBy, setSortBy] = useState<'name' | 'joinDate' | 'bonusEarned' | 'totalDeposited'>('joinDate');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [showFilters, setShowFilters] = useState(false);

    // Get current user - prefer authStoreUser if available
    const currentUser = authStoreUser;
    const referralCode = authStoreUser?.referralCode

    useEffect(() => {
        if (currentUser) {
            fetchReferralData();
        }
    }, [currentUser]);

    const fetchReferralData = async () => {
        try {
            setLoading(true);



            const response = await api.referral.getStats();
            if (response.success && response.data) {
                setReferralStats(response.data);
            }


        } catch (error) {
            console.error('Error fetching referral data:', error);
        } finally {
            setLoading(false);
        }
    };


    const copyReferralCode = async () => {
        if (!referralCode) return;
        try {
            const text = `https://tradeenrich.com/register?ref=${referralCode}`;

            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    // const shareReferralCode = async () => {
    //     const shareData = {
    //         title: 'Join Trade Enrich',
    //         text: `Join me on Trade Enrich and start growing your savings! Use my referral code: ${referralCode}`,
    //         url: `${window.location.origin}/register?ref=${referralCode}`
    //     };

    //     try {
    //         if (navigator.share) {
    //             await navigator.share(shareData);
    //             setShareSuccess(true);
    //             setTimeout(() => setShareSuccess(false), 2000);
    //         } else {
    //             // Fallback to copying the referral link
    //             await navigator.clipboard.writeText(`${window.location.origin}/register?ref=${referralCode}`);
    //             setShareSuccess(true);
    //             setTimeout(() => setShareSuccess(false), 2000);
    //         }
    //     } catch (error) {
    //         console.error('Error sharing:', error);
    //     }
    // };

    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Filter and search logic
    const filteredAndSortedUsers = useMemo(() => {
        let filtered = referralStats?.referredUsers;
        if (!filtered) return;

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(user =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(user => user.status === statusFilter);
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            let cutoffDate: Date;

            switch (dateFilter) {
                case 'week':
                    cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                case 'month':
                    cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    break;
                case 'quarter':
                    cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                    break;
                default:
                    cutoffDate = new Date(0);
            }

            filtered = filtered.filter(user => new Date(user.joinDate) >= cutoffDate);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortBy) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'joinDate':
                    aValue = new Date(a.joinDate);
                    bValue = new Date(b.joinDate);
                    break;
                case 'bonusEarned':
                    aValue = a.bonusEarned;
                    bValue = b.bonusEarned;
                    break;
                case 'totalDeposited':
                    aValue = a.totalDeposited;
                    bValue = b.totalDeposited;
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });

        return filtered;
    }, [referralStats?.referredUsers, searchTerm, statusFilter, dateFilter, sortBy, sortOrder]);

    const handleSort = (field: typeof sortBy) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setDateFilter('all');
        setSortBy('joinDate');
        setSortOrder('desc');
    };

    const hasActiveFilters = searchTerm || statusFilter !== 'all' || dateFilter !== 'all';



    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Header variant="dashboard" />
                <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-400">Loading referral data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        navigate('/');
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Header variant="dashboard" />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Referral Program
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 mt-1">
                        Earn rewards by inviting friends to join Trade Enrich and grow their savings
                    </p>
                </div>

                {/* Referral Code Hero Section */}
                <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 rounded-3xl p-8 mb-8 text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full transform -translate-x-24 translate-y-24"></div>

                    <div className="relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <div className="flex items-center mb-4">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mr-4">
                                        <Users className="h-6 w-6 text-white" />
                                    </div>
                                    <h2 className="text-3xl font-bold">Referral Link</h2>
                                </div>
                                <p className="text-purple-100 mb-6 text-lg">
                                    Share your unique link with friends and earn bonus returns when they join and start saving with Trade Enrich.
                                </p>
                                <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-6 border border-white/30 shadow-xl">
                                    <div className="text-sm text-purple-100 mb-2 uppercase tracking-wide">Your Ref Link</div>
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 w-full">
                                        <span className="font-mono text-sm sm:text-base text-white break-all">https://tradeenrich.com/register?ref={referralCode}</span>
                                        <div className="flex justify-end sm:justify-center space-x-2">
                                            <button
                                                onClick={copyReferralCode}
                                                className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/30 hover:border-white/50"
                                                title="Copy code"
                                            >
                                                {copySuccess ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                                            </button>

                                        </div>
                                    </div>
                                    {(copySuccess) && (
                                        <div className="mt-2 text-sm text-green-200">
                                            {copySuccess ? 'Code copied to clipboard!' : 'Referral link shared!'}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="text-center lg:text-right">
                                <div className="inline-flex flex-col items-center">
                                    <div className="w-24 h-24 bg-white/20 backdrop-blur-xl rounded-3xl flex items-center justify-center mb-6 shadow-2xl">
                                        <Gift className="h-12 w-12 text-white" />
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                                        <div className="text-sm text-purple-200 mb-1">Earn up to</div>
                                        <div className="text-4xl font-bold text-white mb-2">3.5%</div>
                                        <div className="text-sm text-purple-200">on each referral's deposit</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                <Users className="h-6 w-6 text-white" />
                            </div>
                            <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50 px-2 py-1 rounded-full">
                                Total
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {referralStats?.referralStats.totalReferrals || 0}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Referrals</p>
                    </div>

                    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                                <DollarSign className="h-6 w-6 text-white" />
                            </div>
                            <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 px-2 py-1 rounded-full">
                                Earned
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {formatCurrency(referralStats?.referralStats.totalBonus || 0)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Bonus Earned</p>
                    </div>

                    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                                <Clock className="h-6 w-6 text-white" />
                            </div>
                            <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/50 px-2 py-1 rounded-full">
                                Pending
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {formatCurrency(referralStats?.referralStats.pendingBonus || 0)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Pending Bonus</p>
                    </div>

                    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                                <TrendingUp className="h-6 w-6 text-white" />
                            </div>
                            <span className="text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/50 px-2 py-1 rounded-full">
                                This Month
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {formatCurrency(referralStats?.referralStats.thisMonthBonus || 0)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Bonus</p>
                    </div>
                </div>

                {/* How It Works Section */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 mb-8 shadow-2xl border border-white/50 dark:border-gray-700/50">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                        How the Referral Program Works
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                                <Share2 className="h-8 w-8 text-white" />
                            </div>
                            <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">1. Share Your Code</h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Share your unique referral code with friends and family through social media, email, or direct link.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                                <UserPlus className="h-8 w-8 text-white" />
                            </div>
                            <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">2. Friends Join</h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Your friends sign up using your referral code and start their savings journey with Trade Enrich.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                                <Award className="h-8 w-8 text-white" />
                            </div>
                            <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">3. Earn Rewards</h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Earn bonus returns on every deposit your referred friends make. The more they save, the more you earn!
                            </p>
                        </div>
                    </div>
                </div>

                {/* Referred Users List */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/50 dark:border-gray-700/50">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 space-y-4 lg:space-y-0">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                            <Users className="h-6 w-6 mr-3 text-blue-600" />
                            Your Referred Users ({filteredAndSortedUsers?.length}{referralStats?.referredUsers.length !== filteredAndSortedUsers?.length ? ` of ${referralStats?.referredUsers.length}` : ''})
                        </h3>

                        {/* Search and Filter Controls */}
                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                            {/* Search Input */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full sm:w-64 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Filter Toggle */}
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${hasActiveFilters || showFilters
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Filter className="h-4 w-4" />
                                <span>Filters</span>
                                {hasActiveFilters && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4 mb-6 border border-gray-200 dark:border-gray-600">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Status Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Status
                                    </label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value as any)}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="pending">Pending</option>
                                    </select>
                                </div>

                                {/* Date Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Join Period
                                    </label>
                                    <select
                                        value={dateFilter}
                                        onChange={(e) => setDateFilter(e.target.value as any)}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white"
                                    >
                                        <option value="all">All Time</option>
                                        <option value="week">Last Week</option>
                                        <option value="month">Last Month</option>
                                        <option value="quarter">Last Quarter</option>
                                    </select>
                                </div>

                                {/* Sort Options */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Sort By
                                    </label>
                                    <select
                                        value={`${sortBy}-${sortOrder}`}
                                        onChange={(e) => {
                                            const [field, order] = e.target.value.split('-');
                                            setSortBy(field as any);
                                            setSortOrder(order as any);
                                        }}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white"
                                    >
                                        <option value="joinDate-desc">Join Date (Newest)</option>
                                        <option value="joinDate-asc">Join Date (Oldest)</option>
                                        <option value="name-asc">Name (A-Z)</option>
                                        <option value="name-desc">Name (Z-A)</option>
                                        <option value="bonusEarned-desc">Bonus Earned (High-Low)</option>
                                        <option value="bonusEarned-asc">Bonus Earned (Low-High)</option>
                                        <option value="totalDeposited-desc">Total Deposited (High-Low)</option>
                                        <option value="totalDeposited-asc">Total Deposited (Low-High)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Clear Filters */}
                            {hasActiveFilters && (
                                <div className="flex justify-end mt-4">
                                    <button
                                        onClick={clearFilters}
                                        className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                        <span>Clear Filters</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Results Summary */}
                    {hasActiveFilters && (
                        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                            Showing {filteredAndSortedUsers?.length} of {referralStats?.referredUsers.length} referred users
                        </div>
                    )}

                    {filteredAndSortedUsers?.length === 0 ? (
                        referralStats?.referredUsers.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                    <Users className="h-10 w-10 text-gray-400" />
                                </div>
                                <h4 className="text-gray-900 dark:text-white font-bold text-xl mb-3">No referrals yet</h4>
                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                                    Start sharing your referral code to invite friends and earn bonus returns on their deposits
                                </p>
                                {/* <button
                                    onClick={shareReferralCode}
                                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-2xl text-sm font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                                >
                                    Share Your Code
                                </button> */}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                    <Search className="h-10 w-10 text-gray-400" />
                                </div>
                                <h4 className="text-gray-900 dark:text-white font-bold text-xl mb-3">No matching referrals found</h4>
                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                                    Try adjusting your search terms or filters to find the referrals you're looking for
                                </p>
                                <button
                                    onClick={clearFilters}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium transition-colors"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        )
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            <button
                                                onClick={() => handleSort('name')}
                                                className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                            >
                                                <span>User</span>
                                                {sortBy === 'name' && (
                                                    sortOrder === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                                                )}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            <button
                                                onClick={() => handleSort('joinDate')}
                                                className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                            >
                                                <span>Join Date</span>
                                                {sortBy === 'joinDate' && (
                                                    sortOrder === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                                                )}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            <button
                                                onClick={() => handleSort('totalDeposited')}
                                                className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                            >
                                                <span>Total Deposited</span>
                                                {sortBy === 'totalDeposited' && (
                                                    sortOrder === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                                                )}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            <button
                                                onClick={() => handleSort('bonusEarned')}
                                                className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                            >
                                                <span>Bonus Earned</span>
                                                {sortBy === 'bonusEarned' && (
                                                    sortOrder === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                                                )}
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {filteredAndSortedUsers?.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                                        <span className="text-white font-medium text-sm">
                                                            {user.name.split(' ').map(n => n[0]).join('')}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {formatDate(user.joinDate)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.status === 'active'
                                                    ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/50'
                                                    : 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/50'
                                                    }`}>
                                                    {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">
                                                {formatCurrency(user.totalDeposited)}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-semibold text-green-600 dark:text-green-400">
                                                {formatCurrency(user.bonusEarned)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
